import { Injectable } from '@angular/core';
import { Store } from '../../../services/store';
import { concat, last, map, Observable, of, switchMap, tap } from 'rxjs';
import { BomItem } from '../../../models/bom-item.model';
import { ClientOrderProvider } from './client-order.provider';
import { ClientOrderHeader } from './models/client-order.dto';
import { addChild, ClientOrderItemVm, ClientOrderVm, findNode, productionOrderName, remainingAmount, removeNode, replaceNode, ShipmentLineVm, unshippedAmount, updateNode, withShipmentLines } from './models/client-order.view-model';
import { ShipmentStatus } from '../shipment/models/shipment.dto';
import { ShipmentListItemDto } from '../shipments/models/shipment-list-item.dto';
import { guid } from '../../../utils/guid';
import { toVoid } from '../../../utils/void';
import { parseAmount } from '../../../utils/format-amount';
import { Point } from '../../../models/point';
import { BomItemSelected } from '../../add-node-dropdown/add-node-dropdown.component';

export interface ClientOrderState {
  loading: boolean;
  /**
   * Undefined until the header is created — positions can only be added to a saved order.
   */
  order: ClientOrderVm | undefined;
  // customization popups over the position trees, wired like BomTreeStore's
  addNodeDropdown: {
    triggerNodeId: string;
    position: Point;
  } | undefined;
  contextMenu: {
    nodeId: string;
    position: Point;
  } | undefined;
}

const INITIAL_STATE: ClientOrderState = {
  loading: false,
  order: undefined,
  addNodeDropdown: undefined,
  contextMenu: undefined
};

@Injectable({ providedIn: 'root' })
export class ClientOrderStore extends Store<ClientOrderState> {
  constructor(private provider: ClientOrderProvider) {
    super(INITIAL_STATE);
  }

  init(id: number | 'new'): Observable<void> {
    if (id === 'new') {
      return of(undefined);
    }

    this.update(state => ({ ...state, loading: true }));

    return this.provider.getClientOrder(id)
      .pipe(
        map(ClientOrderVm.mapModelToViewModel),
        tap(order => {
          this.update(state => ({ ...state, order, loading: false }));
        }),
        toVoid()
      );
  }

  createOrder(header: ClientOrderHeader): Observable<void> {
    return this.provider.createClientOrder(header)
      .pipe(
        tap(header => {
          // a fresh order has no positions yet — the header response is the whole order
          this.update(state => ({ ...state, order: ClientOrderVm.mapModelToViewModel({ ...header, items: [] }) }));
        }),
        toVoid()
      );
  }

  /**
   * The whole header travels each time — the view shows the new values right away and
   * the backend gets the same shape CreateClientOrder took.
   */
  patchHeader(header: ClientOrderHeader): Observable<void> {
    const order = this.state.order;

    if (!order) {
      throw new Error('Cannot edit the header of an order that is not saved yet.');
    }

    this.update(state => ({ ...state, order: { ...state.order!, ...header } }));

    return this.provider.patchClientOrder(order.id, header);
  }

  // --- confirmation (0016): the one stored status, the ship / un-ship shape ---

  confirm(): Observable<void> {
    const order = this.state.order;

    if (!order) {
      throw new Error('Cannot confirm an order that is not saved yet.');
    }

    // the backend stamps the exact time; the page only needs the day
    this.update(state => ({ ...state, order: { ...state.order!, confirmedAt: new Date().toISOString() } }));

    return this.provider.confirmClientOrder(order.id);
  }

  unconfirm(): Observable<void> {
    const order = this.state.order;

    if (!order) {
      throw new Error('Cannot un-confirm an order that is not saved yet.');
    }

    this.update(state => ({ ...state, order: { ...state.order!, confirmedAt: null } }));

    return this.provider.unconfirmClientOrder(order.id);
  }

  searchBomItems(search: string): Observable<BomItem[]> {
    return this.provider.searchBomItems(search);
  }

  /**
   * Snapshots the chosen BOM tree as a new position, ordered as one piece until the
   * root amount is edited (orders-new precedent).
   */
  addPosition(bomItemId: number, name: string, unit: string): Observable<void> {
    const order = this.state.order;

    if (!order) {
      throw new Error('Cannot add a position to an order that is not saved yet.');
    }

    const placeholderId = this.insertPlaceholder(bomItemId, name, unit);

    return this.provider.createClientOrderItem(order.id, bomItemId, 1)
      .pipe(
        tap(item => {
          const itemVm = ClientOrderItemVm.mapCreatedToViewModel(item);

          this.update(state => ({
            ...state,
            order: {
              ...state.order!,
              items: state.order!.items.map(x => x.id === placeholderId ? itemVm : x)
            }
          }));
        }),
        toVoid()
      );
  }

  /**
   * JIT BOM: the typed name enters the catalog first, then the fresh (leaf) item is
   * snapshotted as a position like any other.
   */
  createBomItemAndAddPosition(name: string): Observable<void> {
    return this.provider.createBomItem(name)
      .pipe(
        // a JIT item enters the catalog with the API's default unit
        switchMap(({ bomItemId }) => this.addPosition(bomItemId, name, 'szt'))
      );
  }

  /**
   * Changes a node's amount — the ordered quantity on a position, per-one-parent on a
   * child (BOM-style, the same meaning the backend gives the request). The view-model
   * changes one field at any depth; the backend rescales the subtree totals itself.
   */
  updateAmount(nodeId: string, value: string): Observable<void> {
    const amount = parseAmount(value);
    const order = this.state.order;
    const node = order ? findNode(order.items, nodeId) : undefined;

    if (!order || !node?.data.clientOrderItemId) {
      throw new Error('Cannot change the amount of a node that is not saved yet.');
    }

    if (!(amount > 0)) {
      return of(undefined);
    }

    this.updateTree(updateNode(nodeId, x => ({ ...x, data: { ...x.data, amount } })));

    return this.provider.patchClientOrderItemAmount(order.id, node.data.clientOrderItemId, amount);
  }

  // --- pegging (0014): the mapping workspace ---

  /**
   * Unlink: the allocation goes, the position's demand is free again. The production
   * item is not touched — it stays as ad-hoc work until the plannist deletes it there.
   */
  deleteAllocation(positionId: string, allocationId: number): Observable<void> {
    const order = this.state.order;

    if (!order) {
      throw new Error('Cannot unlink on an order that is not saved yet.');
    }

    this.updateTree(updateNode(positionId, position => {
      const allocations = position.data.allocations.filter(x => x.allocationId !== allocationId);

      return {
        ...position,
        data: {
          ...position.data,
          allocations,
          allocatedAmount: allocations.reduce((sum, x) => sum + x.amount, 0)
        }
      };
    }));

    return this.provider.deleteAllocation(order.id, allocationId);
  }

  /**
   * "Utwórz zlecenie": one header-only zlecenie, then a client-sourced item per position
   * with remaining demand, each covering exactly that remainder. Sequential on purpose —
   * the backend checks the remainder per call. Emits the new zlecenie's id; the page
   * navigates there, so the store's copy of the positions is not updated here.
   * The zlecenie takes the zamówienie's termin as is and a `{product} (+N) – {client}`
   * name (0114), so the planner row reads like the seeded ones.
   */
  createProductionOrderFromRemaining(): Observable<number> {
    const order = this.state.order;

    if (!order) {
      throw new Error('Cannot create a production order from an order that is not saved yet.');
    }

    const open = order.items.filter(position => remainingAmount(position) > 0);

    return this.provider.createProductionOrder(productionOrderName(open, order.clientName), order.deadline)
      .pipe(
        switchMap(created => concat(
          of(created.id),
          ...open.map(position => this.provider
            .createOrderItemFromPosition(created.id, position.data.clientOrderItemId, remainingAmount(position))
            .pipe(map(() => created.id)))
        )),
        last()
      );
  }

  // --- shipping (0015): the third link on a position ---

  /**
   * The planned wysyłki the position can still be added to — one line per position per
   * shipment, so the ones already carrying it are left out — narrowed by number or
   * transport. The list endpoint is small enough to filter here.
   */
  searchPlannedShipments(positionId: string, search: string): Observable<ShipmentListItemDto[]> {
    const order = this.state.order;
    const position = order ? findNode(order.items, positionId) : undefined;
    const alreadyOn = new Set(position?.data.shipments.map(x => x.shipmentId));
    const term = search.trim().toLowerCase();

    return this.provider.getShipments()
      .pipe(
        map(shipments => shipments.filter(shipment => shipment.status === ShipmentStatus.planned
          && !alreadyOn.has(shipment.id)
          && (!term || shipment.shipmentNumber.toLowerCase().includes(term) || shipment.transport.toLowerCase().includes(term))))
      );
  }

  /**
   * "Dodaj do wysyłki": a line at the position's unshipped remainder on the chosen
   * planned wysyłka. The row appears at once; the amount is edited afterwards, on the
   * row here or on the shipment page.
   */
  addToShipment(positionId: string, shipment: ShipmentListItemDto): Observable<void> {
    const order = this.state.order;
    const position = order ? findNode(order.items, positionId) : undefined;

    if (!order || !position?.data.clientOrderItemId) {
      throw new Error('Cannot add a position that is not saved yet to a shipment.');
    }

    const amount = unshippedAmount(position);

    const placeholder = ShipmentLineVm.mapModelToViewModel({
      shipmentItemId: 0,
      shipmentId: shipment.id,
      shipmentNumber: shipment.shipmentNumber,
      transport: shipment.transport,
      amount,
      unit: position.data.unit,
      status: shipment.status,
      plannedDate: shipment.plannedDate,
      shippedAt: shipment.shippedAt
    });

    this.updateTree(updateNode(positionId, x => withShipmentLines(x, [...x.data.shipments, placeholder])));

    return this.provider.createShipmentItem(shipment.id, position.data.clientOrderItemId, amount)
      .pipe(
        tap(line => {
          // one line per position per shipment, so the shipment id finds the placeholder
          this.updateTree(updateNode(positionId, x => withShipmentLines(
            x,
            x.data.shipments.map(s => s.shipmentId === shipment.id ? { ...s, shipmentItemId: line.shipmentItemId } : s))));
        }),
        toVoid()
      );
  }

  /**
   * "Usuń z wysyłki": the line goes, the amount is free to ship again.
   */
  removeFromShipment(positionId: string, line: ShipmentLineVm): Observable<void> {
    const order = this.state.order;

    if (!order) {
      throw new Error('Cannot remove from a shipment on an order that is not saved yet.');
    }

    this.updateTree(updateNode(positionId, x => withShipmentLines(
      x,
      x.data.shipments.filter(s => s.shipmentItemId !== line.shipmentItemId))));

    return this.provider.deleteShipmentItem(line.shipmentId, line.shipmentItemId);
  }

  /**
   * "Utwórz wysyłkę": one header-only wysyłka, then a line per position with an
   * unshipped remainder, each at exactly that remainder. Sequential on purpose — the
   * backend checks the remainder per call. Emits the new wysyłka's id; the page
   * navigates there, so the store's copy of the positions is not updated here.
   */
  createShipmentFromUnshipped(transport: string): Observable<number> {
    const order = this.state.order;

    if (!order) {
      throw new Error('Cannot create a shipment from an order that is not saved yet.');
    }

    const open = order.items.filter(position => unshippedAmount(position) > 0);

    return this.provider.createShipment({ transport, plannedDate: null })
      .pipe(
        switchMap(created => concat(
          of(created.id),
          ...open.map(position => this.provider
            .createShipmentItem(created.id, position.data.clientOrderItemId, unshippedAmount(position))
            .pipe(map(() => created.id)))
        )),
        last()
      );
  }

  // --- customization: context menu + add-child dropdown, wired like BomTreeStore ---

  openContextMenu(nodeId: string, position: Point): void {
    this.update(state => ({
      ...state,
      // a right-click is not a 'click', so the dropdown's clickOutside never fires — close it here
      addNodeDropdown: undefined,
      contextMenu: {
        nodeId,
        position
      }
    }));
  }

  closeContextMenu(): void {
    this.update(state => ({ ...state, contextMenu: undefined }));
  }

  openAddNodeDropdown(triggerNodeId: string, position: Point): void {
    this.update(state => ({
      ...state,
      addNodeDropdown: {
        triggerNodeId,
        position
      }
    }));
  }

  closeAddNodeDropdown(): void {
    this.update(state => ({ ...state, addNodeDropdown: undefined }));
  }

  /**
   * Grafts a BOM item's subtree under the node the dropdown was opened on. A name the
   * catalog does not know enters it first (JIT BOM) — as a catalog item only, never as
   * a relation under the parent's BOM: the BOM is a template, the snapshot is what changes.
   */
  addChildFromDropdownItem(newChild: BomItemSelected): Observable<void> {
    const parentNodeId = this.state.addNodeDropdown?.triggerNodeId;

    if (!parentNodeId) {
      throw new Error('Cannot add child from dropdown when dropdown is closed (cannot define parent node).');
    }

    if (newChild.bomItemId) {
      return this.addChild(parentNodeId, newChild.bomItemId, newChild.name);
    }

    return this.provider.createBomItem(newChild.name)
      .pipe(
        switchMap(({ bomItemId }) => this.addChild(parentNodeId, bomItemId, newChild.name))
      );
  }

  /**
   * Removes a node with its whole subtree; on a root, the whole position.
   */
  deleteNode(nodeId: string): Observable<void> {
    const order = this.state.order;
    const node = order ? findNode(order.items, nodeId) : undefined;

    if (!order || !node?.data.clientOrderItemId) {
      throw new Error('Cannot delete a node that is not saved yet.');
    }

    this.update(state => ({
      ...state,
      order: {
        ...state.order!,
        items: state.order!.items
          .filter(x => x.id !== nodeId)
          .map(removeNode(nodeId))
      }
    }));

    return this.provider.deleteClientOrderItem(order.id, node.data.clientOrderItemId);
  }

  private addChild(parentNodeId: string, bomItemId: number, name: string): Observable<void> {
    const order = this.state.order;
    const parent = order ? findNode(order.items, parentNodeId) : undefined;

    if (!order || !parent?.data.clientOrderItemId) {
      throw new Error('Cannot add a child under a node that is not saved yet.');
    }

    // grafted with per-parent amount 1 and edited afterwards, like positions
    const placeholder: ClientOrderItemVm = {
      id: guid(),
      data: {
        clientOrderItemId: 0,
        bomItemId,
        name,
        // the add-node dropdown names the item only; the response brings its unit
        unit: 'szt',
        amount: 1,
        allocatedAmount: 0,
        allocations: [],
        inShipmentsAmount: 0,
        shippedAmount: 0,
        shipments: [],
        primaryPhotoAttachmentId: null
      },
      children: []
    };

    this.updateTree(addChild(parentNodeId, placeholder));

    return this.provider.createClientOrderItemChild(order.id, parent.data.clientOrderItemId, bomItemId, 1)
      .pipe(
        tap(item => {
          this.updateTree(replaceNode(placeholder.id, ClientOrderItemVm.mapCreatedToViewModel(item)));
        }),
        toVoid()
      );
  }

  private updateTree(updatePosition: (root: ClientOrderItemVm) => ClientOrderItemVm): void {
    this.update(state => ({
      ...state,
      order: {
        ...state.order!,
        items: state.order!.items.map(updatePosition)
      }
    }));
  }

  private insertPlaceholder(bomItemId: number, name: string, unit: string): string {
    const id = guid();

    this.update(state => ({
      ...state,
      order: {
        ...state.order!,
        items: [...state.order!.items, {
          id,
          data: {
            clientOrderItemId: 0,
            bomItemId,
            name,
            unit,
            amount: 1,
            allocatedAmount: 0,
            allocations: [],
            inShipmentsAmount: 0,
            shippedAmount: 0,
            shipments: [],
            primaryPhotoAttachmentId: null
          },
          children: []
        }]
      }
    }));

    return id;
  }
}
